import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Card} from '@vio/atlas/src/components/designSystem/Card/Card'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {cssTheme} from '@vio/atlas/src/themes'

export type LayoutVariant = 'vertical' | 'horizontal'

export const TOP_OFFERS_COUNT = 3

const StyledCard = styled(Card)`
  display: flex;
  height: 100%;
  width: 100%;
  padding: ${cssTheme.layout.spacing.s600};
  &.--is-primary {
    border-width: 2px;
  }
`

const OfferCardWrapper = ({
  children,
  isPrimary
}: {
  children: React.ReactNode
  isPrimary?: boolean
}) => {
  return (
    <Box width="100%" flexGrow="1" flexBasis="0px">
      <StyledCard
        className={cx(isPrimary && '--is-primary')}
        borderRadius="lg"
        borderColorPath={
          isPrimary ? 'border.brand.primary' : 'border.neutral.secondary'
        }
      >
        {children}
      </StyledCard>
    </Box>
  )
}

type LayoutProps = {
  offersSlot: React.ReactNode[]
  priceWatchSectionSlot?: React.ReactNode
  moreDealsButtonSlot?: React.ReactNode
}

const DefaultLayout = ({
  offersSlot,
  priceWatchSectionSlot,
  moreDealsButtonSlot
}: LayoutProps) => {
  return (
    <Box display="flex" flexDirection="column" gap="s300" width="100%">
      <Box display="flex" flexDirection="row" gap="s300" width="100%">
        {offersSlot}
        {priceWatchSectionSlot}
      </Box>
      {moreDealsButtonSlot}
    </Box>
  )
}
const DesktopXsLayout = ({
  offersSlot,
  priceWatchSectionSlot,
  moreDealsButtonSlot
}: LayoutProps) => {
  return (
    <Box display="flex" flexDirection="column" gap="s300" width="100%">
      <Box display="flex" flexDirection="row" gap="s300" width="100%">
        {offersSlot}
      </Box>
      {moreDealsButtonSlot}
      {priceWatchSectionSlot}
    </Box>
  )
}

interface Props {
  /** Ready-made offer cards; the parent owns offer/split/anchor business logic. */
  offerCards?: React.ReactNode[]
  /** Rendered price-watch card, or null when price-watch is disabled. */
  priceWatchSection?: React.ReactNode
  /** Replaces priceWatchSection in the Desktop XS layout as a full-width row without the card wrapper. */
  xsSection?: React.ReactNode
  moreDealsButtonSlot?: React.ReactNode
}

/**
 * Pure layout for the desktop top-offers grid. It only composes the cards and
 * the price-watch slot; all business logic lives in TopOffersDesktopRedesigned.
 */
export const TopOffersDesktopLayout = ({
  offerCards = [],
  priceWatchSection = null,
  xsSection = null,
  moreDealsButtonSlot = null
}: Props) => {
  const {isDesktopXs} = useDeviceLayout()
  const isXsLayout = isDesktopXs && offerCards.length > 1
  const Layout = isXsLayout ? DesktopXsLayout : DefaultLayout
  const wrappedPriceWatchSection = priceWatchSection ? (
    <OfferCardWrapper>{priceWatchSection}</OfferCardWrapper>
  ) : null

  return (
    <Box
      padding="s400"
      backgroundColor="background.brand.tertiary"
      borderRadius="xl"
      display="flex"
      flexDirection="column"
      gap="s400"
    >
      <Layout
        offersSlot={offerCards.map((offer, index) => (
          <OfferCardWrapper key={index} isPrimary={index === 0}>
            {offer}
          </OfferCardWrapper>
        ))}
        priceWatchSectionSlot={
          isXsLayout && xsSection ? (
            <Box backgroundColor="background.neutral.primary" borderRadius="lg">
              {xsSection}
            </Box>
          ) : (
            wrappedPriceWatchSection
          )
        }
        moreDealsButtonSlot={
          moreDealsButtonSlot ? (
            <Card borderRadius="lg" borderColorPath="border.neutral.secondary">
              {moreDealsButtonSlot}
            </Card>
          ) : null
        }
      />
    </Box>
  )
}
